import {
  FormField,
  VALIDATION_PATTERNS,
  FieldType,
} from './formFieldTypes';

export interface ValidationError {
  [fieldId: number]: string;
}

/**
 * Validates a single form field based on its type and rules
 */
export const validateField = (
  field: FormField,
  value: any,
  allFields?: FormField[],
  selectedValues?: Record<number, any>
): string | null => {
  // Check if field is required
  if (!field.isOptional && (!value || value === '')) {
    return 'This field is required.';
  }

  // Skip validation if value is empty and field is optional
  if (!value || value === '') {
    return null;
  }

  // Type-specific validation
  switch (field.type) {
    case FieldType.EMAIL:
      if (!VALIDATION_PATTERNS.EMAIL.test(value)) {
        return 'Please enter a valid email address.';
      }
      break;

    case FieldType.PHONE:
    case FieldType.PHONE_OTP:
      if (value && typeof value === 'string') {
        const phoneDigits = value.replace(/\D/g, '');
        if (phoneDigits.length < 10) {
          return 'Please enter a valid phone number.';
        }
      }
      // ✅ ADD: check verified flag
      if (selectedValues && field.type === FieldType.PHONE_OTP) {
        const isVerified = selectedValues[`${field.id}_verified`];
        if (!isVerified) {
          return 'Please verify your phone number with OTP.';
        }
      }
      break;

    case FieldType.DATE:
      if (field.system_identifire === 'dateOfBirth') {
        const age = calculateAge(new Date(value));
        if (age < 18) {
          return 'You must be at least 18 years old.';
        }
      }
      break;

    case FieldType.PASSWORD:
      if (value.length < 8) {
        return 'Password must be at least 8 characters long.';
      }
      if (!/[A-Z]/.test(value)) {
        return 'Password must contain at least one uppercase letter.';
      }
      if (!/[0-9]/.test(value)) {
        return 'Password must contain at least one number.';
      }
      break;

    case FieldType.OTP:
      if (value.length < 6) {
        return 'OTP must be 6 digits.';
      }
      if (selectedValues) {
        const isVerified = selectedValues[`${field.id}_verified`];
        if (!isVerified) {
          return 'Please verify your phone number with OTP.';
        }
      }
      break;

    case FieldType.SELECT:
      if (!value || value === '') {
        return 'Please select an option.';
      }
      break;

    case FieldType.RADIO:
      if (!value || value === '') {
        return 'Please select an option.';
      }
      break;

    default:
      break;
  }

  return null;
};

/**
 * Validates all fields in a form step
 */
export const validateFormStep = (
  fieldsToValidate: FormField[],
  selectedValues: Record<number, any>
): ValidationError => {
  const errors: ValidationError = {};

  fieldsToValidate.forEach((field) => {
    if (field.type === FieldType.LINK) {
      return; // Skip link fields
    }

    const value = selectedValues[field.id];
    const error = validateField(field, value, fieldsToValidate, selectedValues);

    if (error) {
      errors[field.id] = error;
    }
  });

  return errors;
};

/**
 * Get fields that need to be validated for current step
 * Includes visible parent fields and their visible child fields
 */
export const getFieldsToValidate = (
  allFields: FormField[],
  selectedValues: Record<number, any>
): FormField[] => {
  const visibleFields: FormField[] = [];

  allFields.forEach((field) => {
    // Add all parent-level fields
    if (!field.parent_question_id) {
      visibleFields.push(field);
    }
  });

  // Add visible child fields
  const childFields = allFields.filter((field) => field.parent_question_id);
  childFields.forEach((field) => {
    if (isFieldVisible(field, allFields, selectedValues)) {
      visibleFields.push(field);
    }
  });

  return visibleFields;
};

/**
 * Calculate age from date of birth
 */
export const calculateAge = (dateOfBirth: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - dateOfBirth.getFullYear();
  const monthDiff = today.getMonth() - dateOfBirth.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())
  ) {
    age--;
  }

  return age;
};

/**
 * Check if a field should be visible based on parent field value
 */
export const isFieldVisible = (
  field: FormField,
  allFields: FormField[],
  selectedValues: Record<number, any>
): boolean => {
  if (!field.parent_question_id) {
    return true;
  }

  const parentField = allFields.find((f) => f.id === field.parent_question_id);
  if (!parentField) {
    return false;
  }

  const parentValue = selectedValues[parentField.id];

  // Parent field must have a value
  if (parentValue === undefined || parentValue === null || parentValue === '') {
    return false;
  }

  // Check parent option ID match
  if (field.parent_option_id) {
    const selectedOption = parentField.options?.find(
      (opt) => opt.id === field.parent_option_id
    );

    if (selectedOption) {
      const optionAnswer =
        typeof selectedOption.answer === 'boolean'
          ? selectedOption.answer.toString()
          : selectedOption.answer;
      const parentAnswer =
        typeof parentValue === 'boolean' || typeof parentValue === 'object'
          ? typeof parentValue === 'object'
            ? parentValue.answer?.toString()
            : parentValue.toString()
          : parentValue;

      return optionAnswer === parentAnswer;
    }
  }

  return false;
};

/**
 * Remove duplicate questions (in case of API returning duplicates)
 */
export const removeDuplicateFields = (fields: FormField[]): FormField[] => {
  return fields.filter(
    (field, index, self) => index === self.findIndex((f) => f.id === field.id)
  );
};

/**
 * Get error message for a field
 */
export const getFieldError = (
  fieldId: number,
  validationErrors: ValidationError
): string | null => {
  return validationErrors[fieldId] || null;
};

/**
 * Check if any required field is missing
 */
export const hasRequiredErrors = (errors: ValidationError): boolean => {
  return Object.keys(errors).length > 0;
};
